import React, { useEffect, useState, useRef } from 'react';
import { FaMinus, FaPlus } from 'react-icons/fa';
import { HiDownload } from 'react-icons/hi';
import { IoMdClose } from 'react-icons/io';
import ReactDOM from 'react-dom';

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.85)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
  height: '100vh',
  width: '100vw'
};

const imgStyle = {
  maxWidth: '90%',
  maxHeight: '90%',
  boxShadow: '0 2px 10px rgba(0,0,0,0.5)',
  objectFit: 'contain'
};

const buttonStyle = {
  border: 'none',
  color: '#fff',
  fontSize: 28,
  cursor: 'pointer',
  padding: 12,
  background: '#202020'
};

const toolbarStyle = {
  position: 'absolute',
  top: 12,
  right: 12,
  display: 'flex',
  alignItems: 'center',
  background: '#202020'
};

toolbarStyle.zIndex = 1000;
toolbarStyle.pointerEvents = 'auto';

const FlexLightbox = ({
  images = [],
  index = 0,
  onClose = () => {},
  onPrev = () => {},
  onNext = () => {},
  description = ''
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const imgRef = useRef(null);

  const src = images[index] || images[0];

  useEffect(() => {
    setIsLoading(true);
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, [src]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };

    document.addEventListener('keydown', handleKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose, onPrev, onNext]);

  return ReactDOM.createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Lightbox"
      style={overlayStyle}
    >
      <div style={toolbarStyle}>
        <button
          aria-label="Zoom out"
          title="Zoom out"
          style={buttonStyle}
          onClick={(e) => {
            e.stopPropagation();
            setScale((s) => Math.max(1, +(s - 0.25).toFixed(2)));
          }}
        >
          <FaMinus size={12} />
        </button>
        <button
          aria-label="Zoom in"
          title="Zoom in"
          style={buttonStyle}
          onClick={(e) => {
            e.stopPropagation();
            setScale((s) => Math.min(3, +(s + 0.25).toFixed(2)));
          }}
        >
          <FaPlus size={12} />
        </button>
        <div style={{ color: '#fff', margin: '0 8px' }}>
          {Math.round(scale * 100)}%
        </div>
        <a
          href={src}
          style={{ color: '#fff', fontSize: 18, marginRight: 8 }}
          onClick={(e) => {
            e.preventDefault();
            const link = document.createElement('a');
            fetch(src)
              .then((response) => response.blob())
              .then((blob) => {
                const url = window.URL.createObjectURL(blob);
                link.href = url;
                const fileName = src.split('/').pop().split('?')[0];
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
              })
              .catch((error) => {
                console.error('Erro ao baixar o arquivo:', error);
              });
          }}
        >
          <HiDownload size={24} />
        </a>

        <button
          aria-label="Fechar"
          style={buttonStyle}
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <IoMdClose size={24} />
        </button>
      </div>

      {isLoading && (
        <div
          aria-hidden={true}
          style={{
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            inset: 0
          }}
        >
          <div style={{ textAlign: 'center', color: '#fff' }}>
            <svg
              width="56"
              height="56"
              viewBox="0 0 50 50"
              style={{ marginBottom: 8 }}
            >
              <circle
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke="#fff"
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray="31.4 31.4"
              >
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0 25 25"
                  to="360 25 25"
                  dur="1s"
                  repeatCount="indefinite"
                />
              </circle>
            </svg>
            <div>Carregando...</div>
          </div>
        </div>
      )}

      {/\.(mp4|webm|ogg|mov|mkv|avi)(\?.*)?$/i.test(src) ? (
        <video
          src={src}
          controls
          style={{ ...imgStyle, visibility: isLoading ? 'hidden' : 'visible' }}
          onClick={(e) => e.stopPropagation()}
          onLoadedData={() => setIsLoading(false)}
          onCanPlay={() => setIsLoading(false)}
          onError={() => setIsLoading(false)}
        />
      ) : (
        <img
          ref={imgRef}
          src={src}
          alt={description}
          style={{
            ...imgStyle,
            visibility: isLoading ? 'hidden' : 'visible',
            transform: `scale(${scale})`,
            transition: isPanning ? 'none' : 'transform 0.12s ease-out'
          }}
          onLoad={() => {
            setIsLoading(false);
          }}
          onError={() => {
            setIsLoading(false);
          }}
        />
      )}

      <button
        aria-label="Prev"
        onClick={(e) => {
          e.stopPropagation();
          onPrev();
        }}
        style={{
          position: 'absolute',
          left: `calc(5% - ${(scale - 1) * 45}%)`,
          top: '50%',
          transform: `translateY(-50%) scale(${scale})`,
          ...buttonStyle,
          zIndex: 10020,
          pointerEvents: 'auto'
        }}
      >
        ‹
      </button>

      <button
        aria-label="Next"
        onClick={(e) => {
          e.stopPropagation();
          onNext();
        }}
        style={{
          position: 'absolute',
          right: `calc(5% - ${(scale - 1) * 45}%)`,
          top: '50%',
          transform: `translateY(-50%) scale(${scale})`,
          ...buttonStyle,
          zIndex: 10020,
          pointerEvents: 'auto'
        }}
      >
        ›
      </button>

      {description && (
        <div
          style={{
            position: 'absolute',
            bottom: 20,
            color: '#fff',
            textAlign: 'center',
            width: '100%'
          }}
        >
          {description}
        </div>
      )}
    </div>,
    document.body
  );
};

export default FlexLightbox;
